'use client';

import { logger } from '@magicschool/logger';
import { toast } from '@magicschool/spellbook';
import { mapSupabaseUserToSessionClaimsUser } from '@magicschool/supabase/user';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { handleSignOut } from '@/features/auth/helpers';
import { useStore } from '@/features/store';
import { useAuth } from '@/hooks/useAuth';
import { getCookieValue } from '@/util/cookies';
import { IMPERSONATING_COOKIE, isImpersonating } from '@/util/impersonation';
import { clientSupabase } from '@/util/supabase';

export function AuthSessionSync() {
  const pathname = usePathname();
  const intl = useIntl();
  const { userId } = useAuth();
  const setAuthField = useStore((state) => state.AuthStoreData.setField, []);
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const syncInFlightRef = useRef(false);
  const syncPendingRef = useRef(false);

  const syncSession = useCallback(async () => {
    if (syncInFlightRef.current) {
      syncPendingRef.current = true;
      return;
    }

    syncInFlightRef.current = true;

    try {
      const zustandUserId = userIdRef.current;
      const impersonating = isImpersonating(getCookieValue(IMPERSONATING_COOKIE));

      const readClientSession = async () => {
        const {
          data: { session },
          error,
        } = await clientSupabase.auth.getSession();

        if (error) {
          throw error;
        }

        return session;
      };

      let session = await readClientSession();

      // Null session during token refresh is not a mismatch — wait for TOKEN_REFRESHED.
      if (zustandUserId && session === null) {
        return;
      }

      const sessionUserId = session?.user?.id;
      if (!impersonating && zustandUserId && sessionUserId && sessionUserId !== zustandUserId) {
        session = await readClientSession();
        if (session === null) {
          return;
        }
        const retryUserId = session?.user?.id;
        if (retryUserId && retryUserId !== zustandUserId) {
          toast({ title: intl.formatMessage({ id: 'toast.expired-session' }), status: 'error' });
          await handleSignOut();
          return;
        }
      }

      if (session?.user) {
        setAuthField('session')(mapSupabaseUserToSessionClaimsUser(session.user));
      }
    } catch (error) {
      logger.warn('[AuthSessionSync] Failed to read client session', {
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      syncInFlightRef.current = false;
      if (syncPendingRef.current) {
        syncPendingRef.current = false;
        void syncSession();
      }
    }
  }, [intl, setAuthField]);

  // pathname intentionally triggers resync on navigation (PLAT-1629)
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the resync trigger
  useEffect(() => {
    void syncSession();
  }, [pathname, syncSession]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void syncSession();
      }
    };

    const onFocus = () => {
      void syncSession();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [syncSession]);

  useEffect(() => {
    const {
      data: { subscription },
    } = clientSupabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setAuthField('session')(undefined);
        return;
      }

      if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
        void syncSession();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [setAuthField, syncSession]);

  return null;
}
